import React, { useEffect, useRef, useState } from 'react'
import { Stack } from 'react-bootstrap'
import defaultImage from '../../../_metronic/images/uploadImageIcon.svg'
import { BUCKET_NAME } from '../../../utils/config'
import ResetPassword from './ResetPassword'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import {
  GET_USER_PROFILE,
  GETSIGNEDURLFORUPLOAD,
  S3_FILE_DELETE,
  UPDATE_USER_PROFILE,
} from '../../../utils/GraphQLAPI'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import TextField from '../TextField/TextField'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import './UserProfile.css'
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs'

const MySwal = withReactContent(Swal)
const networkOnly = 'network-only'

const initialValues = {
  avatarUrl: '',
  firstName: '',
  lastName: '',
  emailId: '',
  teamName: '',
  salesChannel: '',
}

const SpecialChar = 'Special characters not allowed'
const enrollSchema = Yup.object().shape({
  firstName: Yup.string()
    .required('First name is required')
    .matches(/^[0-9a-zA-Z \b]+$/, SpecialChar)
    .min(3, "First name shouldn't less than 3 characters")
    .max(40, "First name shouldn't exceed 40 characters")
    .nullable(),
  lastName: Yup.string()
    .matches(/^[0-9a-zA-Z \b]+$/, SpecialChar)
    .nullable(),
  emailId: Yup.string()
    .required('Email ID is required')
    .email('Email ID is not Valid')
    .matches(/^(?!.*@[^,]*,)/, 'Email ID is not Valid')
    .min(10, "Email ID shouldn't less than 10 characters")
    .max(40, "Email ID shouldn't exceed 40 characters")
    .nullable(),
  salesChannel: Yup.string()
    .matches(/^[0-9a-zA-Z \b]+$/, SpecialChar)
    .min(3, "Channel Name shouldn't less than 3 characters")
    .max(40, "Channel Name shouldn't exceed 40 characters")
    .nullable(),
  teamName: Yup.string()
    .matches(/^[0-9a-zA-Z \b]+$/, SpecialChar)
    .matches(/^[^-\s][a-zA-Z0-9_\s-]+$/, 'Space not allowed')
    .min(3, "Team name shouldn't less than 3 characters")
    .max(40, "Team name shouldn't exceed 40 characters")
    .nullable(),
})

const UserProfile = () => {
  const [uploadFileName, setUploadFileName] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [btnShow, setBtnShow] = useState<boolean>(false)
  const [textShow, setTextShow] = useState<boolean>(false)
  const [userRole, setUserRole] = useState<string>('')
  const [image, setImage] = useState<File | null>(null)
  const [imageUrl, setImageUrl] = useState<string>('')
  const [preview, setPreview] = useState<string>('')
  const [data, setData] = useState<any>(null)
  const [isDisabled, setIsDisabled] = useState<boolean>(true)
  const [fileError, setFileError] = useState<string>('')
  const [editMode, setEditMode] = useState<boolean>(false)
  const [deleteCall, setDeleteCall] = useState<boolean>(false)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [pendingImageUpload, setPendingImageUpload] = useState<boolean>(false)

  const handleEdit = () => {
    setBtnShow(true)
    setTextShow(true)
    setEditMode(true)
    setIsDisabled(false)
    setIsUploading(false)
    setPendingImageUpload(false)
    formik.setFieldValue('avatarUrl', data?.getUserProfile?.avatarUrl)
  }

  const handleDiscard = () => {
    setImage(null)
    setBtnShow(false)
    setTextShow(false)
    setIsDisabled(true)
    setEditMode(false)
    setFileError('')
    setIsUploading(false)
    setPendingImageUpload(false)
    setDeleteCall(false)
    setImageUrl('')
    setPreview(data?.getUserProfile?.avatarUrl ?
      process.env.REACT_APP_IMAGE_BASE_URL + data?.getUserProfile?.avatarUrl :
      defaultImage)
    formik.setErrors({
      avatarUrl: '',
      firstName: '',
      lastName: '',
      emailId: '',
      teamName: '',
      salesChannel: '',
    })
    formik.resetForm()
    getUserProfile()
    formik.setFieldValue('avatarUrl', data?.getUserProfile?.avatarUrl)
  }

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>, name: string) => {
    formik.setFieldValue(name, e.target.value)
  }

  // Fetch User Data
  const [getUserProfile] = useLazyQuery(GET_USER_PROFILE, {
    fetchPolicy: networkOnly,
    variables: {
      getEntityName: true,
    },
    onCompleted: async (userData) => {
      window.localStorage.setItem('userId', JSON.stringify(userData?.getUserProfile?.id))
      const userDetails = {
        avatarUrl: userData?.getUserProfile?.avatarUrl,
        firstName: userData?.getUserProfile?.firstName,
        lastName: userData?.getUserProfile?.lastName,
        emailId: userData?.getUserProfile?.emailId,
        salesChannel:
          userData?.getUserProfile?.members[0]?.role === 'SALES_ADMIN' ||
            userData?.getUserProfile?.members[0]?.role === 'AGENT'
            ? userData?.getUserProfile?.members[0]?.entityName
            : '',
        teamName:
          userData?.getUserProfile?.members[0]?.role === 'SYSTEM_ADMIN' ||
            userData?.getUserProfile?.members[0]?.role === 'QC_USER'
            ? userData?.getUserProfile?.members[0]?.entityName
            : '',
      }
      formik.setValues({ ...userDetails })
      setData(userData)

      if (userData.getUserProfile.userRole === 'TPV_ADMIN') {
        setUserRole('TPV Admin')
      } else if (userData.getUserProfile.userRole === 'USER') {
        if (userData.getUserProfile.members[0].role === 'AGENT') {
          setUserRole('Agent Admin')
        } else if (userData.getUserProfile.members[0].role === 'SALES_ADMIN') {
          setUserRole('Sales Admin')
        } else if (userData.getUserProfile.members[0].role === 'SYSTEM_ADMIN') {
          setUserRole('System Admin')
        } else if (userData.getUserProfile.members[0].role === 'QC_USER') {
          setUserRole('QC User')
        }
      }
    },
  })

  useEffect(() => {
    getUserProfile()
  }, [])

  const formik = useFormik({
    initialValues,
    validationSchema: enrollSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      // Prevent submission if upload is in progress
      if (isUploading || pendingImageUpload) {
        MySwal.fire({
          icon: 'warning',
          title: 'Please wait for image upload to complete',
        })
        return
      }

      // Determine the final avatar URL
      let finalAvatarUrl
      if (deleteCall) {
        finalAvatarUrl = null
      } else if (imageUrl) {
        // New image was uploaded
        finalAvatarUrl = imageUrl
      } else {
        // No new image, keep existing
        finalAvatarUrl = data?.getUserProfile?.avatarUrl
      }

      updateUserProfile({
        variables: {
          updateUserInput: {
            firstName: values.firstName,
            lastName: values.lastName,
            avatarUrl: finalAvatarUrl,
          },
        },
      })
    },
  })

  // Update
  const [updateUserProfile] = useMutation(UPDATE_USER_PROFILE, {
    onCompleted: (detail) => {
      if (detail.updateUserProfile.id) {
        if (deleteCall && data?.getUserProfile?.avatarUrl) {
          // Delete old image from S3
          deleteProfile({
            variables: {
              key: data?.getUserProfile?.avatarUrl,
              bucketName: BUCKET_NAME,
            },
          }).then(() => {
            setDeleteCall(false)
          }).catch(error => {
            console.error('Error deleting old image:', error)
            setDeleteCall(false)
          })
        }

        const options = {
          customClass: {
            popup: 'swal2-border-radius',
          },
          title: 'Profile Updated Successfully',
          icon: 'success' as const,
          confirmButtonColor: '#3E97FF',
        }
        MySwal.fire(options)
        handleDiscard()
      } else {
        MySwal.fire({
          icon: 'error',
          title: 'Error in updating profile',
        })
      }
    },
    onError: (e) => {
      MySwal.fire({
        icon: 'error',
        title: e.message,
      })
    },
  })

  // Handle preview updates
  useEffect(() => {
    if (data?.getUserProfile?.avatarUrl && !image) {
      setPreview(process.env.REACT_APP_IMAGE_BASE_URL + data?.getUserProfile?.avatarUrl)
    } else if (!data?.getUserProfile?.avatarUrl && !image) {
      setPreview(defaultImage)
    }
  }, [data])

  useEffect(() => {
    if (image) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreview(reader.result as string)
      }
      reader.readAsDataURL(image)
    }
  }, [image])

  const {
    loading: urlLoading,
    data: urlData,
    refetch: urlRefetch,
  } = useQuery(GETSIGNEDURLFORUPLOAD, {
    skip: !uploadFileName,
    variables: {
      key: uploadFileName,
      bucketName: BUCKET_NAME,
    },
  })

  const handleUploadImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Reset the input value
    event.target.value = ''

    const allowedImageType = ['image/png', 'image/jpg', 'image/jpeg']
    if (!allowedImageType.includes(file.type)) {
      setFileError('Sorry, you can only upload a .JPG, .JPEG .PNG file')
      return
    }

    // Check if it's an image
    if (file && file.type.startsWith('image/')) {
      const imagesformat = file.name.replace(/\s+/g, '-')
      const imageFileName = imagesformat.split('.').join(`-${Date.now()}.`)

      setUploadFileName(`images/${imageFileName}`)
      setImage(file)
      setFileError('')
      setPendingImageUpload(true)
      setDeleteCall(false)
    } else {
      setImage(null)
      setFileError('Please select a valid image file')
    }
  }

  const fileUploadToS3 = async (objectUrl: string) => {
    if (!objectUrl || !image) return

    setIsUploading(true)
    try {
      const response = await fetch(objectUrl, {
        method: 'PUT',
        headers: { 'Content-Type': image.type },
        body: image,
      })

      if (response.ok) {
        const bucketUrl = response.url.split('?')[0]
        const newImageUrl = bucketUrl.split('com/')[1]
        setImageUrl(newImageUrl)
        setPendingImageUpload(false)
      } else {
        throw new Error('Upload failed')
      }
    } catch (error) {
      console.error('Upload error:', error)
      setFileError('Failed to upload image. Please try again.')
      setPendingImageUpload(false)
    } finally {
      setIsUploading(false)
    }
  }

  useEffect(() => {
    if (!urlLoading && uploadFileName && urlData) {
      setUploadFileName('')
      fileUploadToS3(urlData.getSignedUrlForUpload.preSignedUrl)
    } else if (!urlLoading && uploadFileName && !urlData) {
      urlRefetch()
    }
  }, [urlLoading, uploadFileName, urlData])

  const [deleteProfile] = useLazyQuery(S3_FILE_DELETE, {
    fetchPolicy: 'network-only',
    onCompleted: () => {
      setDeleteCall(false)
    },
    onError: (error) => {
      console.error('Error deleting profile image:', error)
      setDeleteCall(false)
    }
  })

  const deleteS3Object = () => {
    setPreview(defaultImage)
    setImageUrl('')
    setImage(null)
    setDeleteCall(true)
    setPendingImageUpload(false)
  }

  const isFormSubmitDisabled = isUploading || pendingImageUpload || urlLoading

  return (
    <>
      <div className='d-flex justify-content-between align-items-center'>
        <Breadcrumbs title='My Account' subTitle='Home - User Profile' />
      </div>

      <div className='card'>
        <div className='card-body'>
          <div className='row border-bottom mb-3'>
            <div className='col-6'>
              <label className='d-flex align-items-center fs-4 fw-bold mb-4 lh-1'>
                <span className='fs-2 fw-bolder text-gray-900'>Profile Details</span>
              </label>
            </div>
            <div className='col-6 text-end'>
              <button
                type='button'
                className={`btn btn-primary text-white btn-active-color-primary btn-md me-1 ${btnShow ? 'd-none' : ''
                  }`}
                onClick={handleEdit}
                data-kt-menu-trigger='click'
                data-kt-menu-placement='bottom-end'
                style={{
                  position: 'absolute',
                  right: '20px',
                  top: '15px',
                  height: '35px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                Edit
              </button>
            </div>
          </div>

          <form noValidate id='kt_modal_create_app_form' onSubmit={formik.handleSubmit}>
            <div className='row mt-1'>
              <div className='col-lg-12'>
                <label className='d-flex align-items-center mb-2'>
                  <span className='fs-6 fw-semibold lh-23px'>Profile Avatar</span>
                </label>
                <Stack direction='horizontal' gap={1}>
                  <div>
                    {!isUploading ? (
                      <Stack direction='horizontal' gap={3}>
                        <div style={{ position: 'relative' }}>
                          <input
                            type='image'
                            src={preview || defaultImage}
                            alt='Profile'
                            width='128'
                            height='128'
                            disabled={isDisabled}
                            style={{
                              position: 'relative',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              border: '2px solid #e1e5e9'
                            }}
                            onClick={(event) => {
                              event.preventDefault()
                              if (!isDisabled) {
                                fileInputRef.current?.click()
                              }
                            }}
                          />

                          {data?.getUserProfile?.avatarUrl &&
                            editMode &&
                            !deleteCall &&
                            !pendingImageUpload && (
                              <button
                                type='button'
                                onClick={deleteS3Object}
                                className={`btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow ${isDisabled ? 'disabled' : ''
                                  }`}
                                disabled={isDisabled}
                                data-bs-toggle='tooltip'
                                title='Remove avatar'
                                style={{
                                  position: 'absolute',
                                  top: '-10px',
                                  right: '-12px',
                                  zIndex: 1
                                }}
                              >
                                <svg
                                  width='12'
                                  height='12'
                                  viewBox='0 0 20 20'
                                  fill='none'
                                  xmlns='http://www.w3.org/2000/svg'
                                >
                                  <g clipPath='url(#clip0_22658_81815)'>
                                    <path
                                      opacity='0.5'
                                      d='M10 8.23254L8.2326 9.99997L0.36628 17.8655C-0.122021 18.3538 -0.122021 19.1455 0.36628 19.6338C0.854542 20.1221 1.64623 20.1221 2.13453 19.6338L10 11.7674L11.7675 9.99997L19.6338 2.13447C20.1221 1.64621 20.122 0.854526 19.6338 0.366225C19.1455 -0.122075 18.3538 -0.122075 17.8655 0.366225L10 8.23254Z'
                                      fill='currentColor'
                                    />
                                    <path
                                      d='M9.99997 8.23254L11.7674 9.99997L19.6337 17.8655C20.122 18.3538 20.122 19.1455 19.6337 19.6338C19.1455 20.1221 18.3538 20.1221 17.8655 19.6338L9.99997 11.7674L8.23254 9.99997L0.366225 2.13447C-0.122075 1.64621 -0.122036 0.854526 0.366225 0.366225C0.854487 -0.122075 1.64617 -0.122075 2.13447 0.366225L9.99997 8.23254Z'
                                      fill='currentColor'
                                    />
                                  </g>
                                  <defs>
                                    <clipPath id='clip0_22658_81815'>
                                      <rect width='20' height='20' fill='currentColor' />
                                    </clipPath>
                                  </defs>
                                </svg>
                              </button>
                            )}

                          <input
                            type='file'
                            className='d-none'
                            ref={fileInputRef}
                            accept='image/png,image/jpg,image/jpeg'
                            onChange={handleUploadImage}
                          />
                        </div>

                        <div>
                          <span
                            className={`fs-6 text-grey-400 text-muted ${textShow ? '' : 'd-none'
                              }`}
                          >
                            Allowed file types:
                            <br />
                            png, jpg, jpeg.
                          </span>

                          {pendingImageUpload && (
                            <div className='text-primary mt-2'>
                              <small>
                                <span className='spinner-border spinner-border-sm me-2' />
                                Preparing upload...
                              </small>
                            </div>
                          )}

                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block text-danger'>
                              {fileError}
                            </div>
                          </div>
                        </div>
                      </Stack>
                    ) : (
                      <div style={{ position: 'relative' }}>
                        <input
                          type='image'
                          src={preview || defaultImage}
                          alt='Uploading...'
                          width='128'
                          height='128'
                          disabled={true}
                          className='imageBlur'
                          style={{
                            objectFit: 'cover',
                            borderRadius: '8px',
                            filter: 'blur(1px)',
                            opacity: 0.7
                          }}
                        />
                        <div
                          className='d-flex align-items-center justify-content-center position-absolute'
                          style={{
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            borderRadius: '8px',
                            color: 'white',
                            fontSize: '12px',
                            flexDirection: 'column'
                          }}
                        >
                          <span className='spinner-border spinner-border-sm mb-1' />
                          Uploading...
                        </div>
                      </div>
                    )}
                  </div>
                </Stack>
              </div>

              <div className='col-lg-6 mb-7 mt-5'>
                <TextField
                  fieldTitle='First Name'
                  filedTitleMandatory={true}
                  fieldName='firstName'
                  placeHolder='Enter first name'
                  maxLength='40'
                  handleOnChange={handleOnChange}
                  formik={formik}
                  disabled={isDisabled}
                  fieldValue={formik.values.firstName}
                />
              </div>

              <div className='col-lg-6 mb-7 mt-5'>
                <TextField
                  fieldTitle='Last Name'
                  filedTitleMandatory={false}
                  fieldName='lastName'
                  placeHolder='Enter last name'
                  maxLength='40'
                  handleOnChange={handleOnChange}
                  formik={formik}
                  disabled={isDisabled}
                  fieldValue={formik.values.lastName}
                />
              </div>

              <div className='col-lg-6 mb-7'>
                <TextField
                  fieldTitle='Email ID'
                  filedTitleMandatory={true}
                  fieldName='emailId'
                  placeHolder='Enter email id'
                  maxLength='50'
                  handleOnChange={handleOnChange}
                  formik={formik}
                  disabled={true}
                  fieldValue={formik.values.emailId}
                />
              </div>

              {(userRole === 'System Admin' || userRole === 'QC User') && (
                <div className='col-lg-6 mb-7'>
                  <TextField
                    fieldTitle='System Admin Name'
                    filedTitleMandatory={true}
                    fieldName='teamName'
                    placeHolder='Enter system admin name'
                    maxLength='50'
                    handleOnChange={handleOnChange}
                    formik={formik}
                    disabled={true}
                    fieldValue={formik.values.teamName}
                  />
                </div>
              )}

              {(userRole === 'Agent Admin' || userRole === 'Sales Admin') && (
                <div className='col-lg-6 mb-7'>
                  <TextField
                    fieldTitle='Sales Channel'
                    filedTitleMandatory={true}
                    fieldName='salesChannel'
                    placeHolder='Enter sales channel'
                    maxLength='50'
                    handleOnChange={handleOnChange}
                    formik={formik}
                    disabled={true}
                    fieldValue={formik.values.salesChannel}
                  />
                </div>
              )}

              <div className={`border-top d-flex flex-stack mt-10 pt-8 ${btnShow ? '' : 'd-none'}`}>
                <div className='me-2'></div>
                <div className='d-flex'>
                  <button
                    type='button'
                    onClick={handleDiscard}
                    className='btn btn-light me-3'
                    disabled={isFormSubmitDisabled}
                  >
                    Discard
                  </button>
                  <button
                    type='submit'
                    disabled={isFormSubmitDisabled}
                    className={`btn btn-lg btn-primary ${isFormSubmitDisabled ? 'disabled' : ''}`}
                  >
                    {isUploading ? (
                      <>
                        <span className='spinner-border spinner-border-sm me-2' />
                        Uploading...
                      </>
                    ) : pendingImageUpload ? (
                      <>
                        <span className='spinner-border spinner-border-sm me-2' />
                        Preparing...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {editMode && <ResetPassword />}
    </>
  )
}

export default UserProfile